import { Box, Button, IconButton, Typography } from '@mui/material';
import { Warning, Info, Close, Error } from '@mui/icons-material';
import { theme } from '@store';
import { amber } from '@mui/material/colors';

type BannerVariants =
  'warning' | 'error' | 'info' | 'success' | 'lightWarning' | 'lightError';

// const BannerToColorMapper: Record<BannerVariants, string> = {
//   warning: theme.palette.warning.main,
//   error: theme.palette.red.main,
//   info: theme.palette.info.main,
//   success: theme.palette.success.main,
// };

const getIcon = (variant: BannerVariants, color: string) => {
  switch (variant) {
    case 'warning':
      return <Warning sx={{ fontSize: '22px', color }} />;
    case 'error':
      return <Error sx={{ fontSize: '22px', color }} />;
    case 'info':
      return <Info sx={{ fontSize: '22px', color }} />;
    case 'success':
      return <Warning sx={{ fontSize: '22px', color }} />;
    case 'lightWarning':
      return <Warning sx={{ fontSize: '22px', color }} />;
    case 'lightError':
      return <Error sx={{ fontSize: '22px', color }} />;
    default:
      return <Warning sx={{ fontSize: '22px', color }} />;
  }
};

type VariantShape = {
  bg: string;
  text: string;
  icon: string;
};

const bannerVariantStyles: Record<BannerVariants, VariantShape> = {
  success: {
    bg: theme.palette.green.main,
    text: theme.palette.success.contrastText,
    icon: theme.palette.success.dark,
  },
  warning: {
    bg: theme.palette.warning.main,
    text: theme.palette.common.white,
    icon: theme.palette.common.white,
  },
  error: {
    bg: theme.palette.error.main,
    text: theme.palette.common.white,
    icon: theme.palette.common.white,
  },
  info: {
    bg: theme.palette.infoLight.main,
    text: theme.palette.infoText.main,
    icon: theme.palette.infoIcon.main,
  },
  lightWarning: {
    bg: amber[100],
    text: theme.palette.darkRed.main,
    icon: theme.palette.warningDark.main,
  },
  lightError: {
    bg: theme.palette.salmon.main,
    text: theme.palette.darkRed.main,
    icon: theme.palette.errorDark.main,
  },
} as const;

interface StatusBannerProps {
  text: string;
  variant: BannerVariants;
  action?: () => void;
  closeAction?: () => void;
  actionText?: string;
  actionLoading?: boolean;
  actionDisabled?: boolean;
  noBottomMargin?: boolean;
  subText?: string;
  textFontSize?: string;
  textFontWeight?: number;
}

export const StatusBanner = ({
  variant,
  text,
  subText,
  actionText,
  action,
  closeAction,
  actionLoading = false,
  noBottomMargin = false,
  actionDisabled = false,
  textFontSize = '14px',
  textFontWeight = 400,
}: StatusBannerProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        bgcolor: bannerVariantStyles[variant].bg,
        alignItems: 'center',
        px: '16px',
        py: '10px',
        mb: noBottomMargin ? '0' : '0.5rem',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
        {getIcon(variant, bannerVariantStyles[variant].icon)}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography
            variant='subtitle1'
            sx={{
              color: bannerVariantStyles[variant].text,
              lineHeight: '22px',
              fontSize: textFontSize,
              fontWeight: textFontWeight,
            }}
          >
            {text}
          </Typography>
          {subText && (
            <Typography
              variant='body2'
              sx={{ color: bannerVariantStyles[variant].text, fontWeight: 400 }}
            >
              {subText}
            </Typography>
          )}
        </Box>
      </Box>
      {action && !closeAction && (
        <Button
          sx={{ color: bannerVariantStyles[variant].text }}
          onClick={action}
          loading={actionLoading}
          disabled={actionDisabled}
        >
          {actionText}
        </Button>
      )}
      {closeAction && !action && (
        <IconButton onClick={closeAction}>
          <Close sx={{ fontSize: '22px', color: 'white' }} />
        </IconButton>
      )}
    </Box>
  );
};
